// src/components/steps/PersonalInfoStep.js
import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

export default forwardRef(function PersonalInfoStep({ setCanProceed }, ref) {
  // Estados do formulário original
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [nascimento, setNascimento] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cpf, setCpf] = useState("");
  const [cidade, setCidade] = useState("");
  const [uf, setUf] = useState("");

  // --- NOVOS ESTADOS PARA AS LISTAS ---
  const [estados, setEstados] = useState([]); // Para armazenar a lista de estados da API
  const [cidades, setCidades] = useState([]); // Para armazenar a lista de cidades da API

  // Estados de controle de UI
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // --- LÓGICA DE BUSCA NA API DO IBGE ---

  // 1. Busca a lista de ESTADOS (UFs) da API do IBGE assim que o componente é montado.
  useEffect(() => {
    fetch(
      "https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome"
    )
      .then((res) => res.json())
      .then((data) => {
        setEstados(data);
      });
  }, []);

  // 2. Busca a lista de CIDADES sempre que o estado (UF) for alterado.
  useEffect(() => {
    if (!uf) {
      setCidades([]);
      return;
    }
    fetch(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`
    )
      .then((res) => res.json())
      .then((data) => {
        setCidades(data);
      });
  }, [uf]);

  // cálculo de hoje e data mínima para 18 anos
  const today = new Date();
  const eighteenAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );
  const maxBirthDate = eighteenAgo.toISOString().split("T")[0];

  // formatadores
  const formatPhone = (v) =>
    v
      .replace(/\D/g, "")
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d{1,4})/, "$1-$2")
      .slice(0, 15);

  const formatCpf = (v) =>
    v
      .replace(/\D/g, "")
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2")
      .slice(0, 14);

  // validação
  const validateFields = () => {
    const errs = {};
    if (!nome) errs.nome = "O nome é obrigatório.";
    if (!email || !/\S+@\S+\.\S+/.test(email)) errs.email = "E‑mail inválido.";
    if (!nascimento) {
      errs.nascimento = "Data de nascimento obrigatória.";
    } else if (nascimento > maxBirthDate) {
      errs.nascimento = "Você deve ter pelo menos 18 anos.";
    }
    if (!telefone || telefone.replace(/\D/g, "").length < 10)
      errs.telefone = "Telefone inválido.";
    if (!cpf || cpf.replace(/\D/g, "").length !== 11) errs.cpf = "CPF inválido.";
    if (!cidade) errs.cidade = "Cidade obrigatória.";
    if (!uf) errs.uf = "Estado obrigatório.";
    return errs;
  };

  // expor getData e validateStep
  useImperativeHandle(ref, () => ({
    getData() {
      return {
        name: nome,
        email,
        birth_date: nascimento,
        phone: telefone,
        cpf_cnpj: cpf,
        city: cidade,
        state: uf,
      };
    },
    validateStep() {
      const errs = validateFields();
      setErrors(errs);
      return Object.keys(errs).length === 0;
    },
  }));

  // atualiza errors e canProceed
  useEffect(() => {
    const errs = validateFields();
    setErrors(errs);
    setCanProceed(Object.keys(errs).length === 0);
  }, [nome, email, nascimento, telefone, cpf, cidade, uf, setCanProceed]);

  // handlers de blur / touched
  const handleBlur = (field) => {
    setTouched((t) => ({ ...t, [field]: true }));
  };

  // Handler genérico para a maioria dos campos
  const handleChange = (setter, value, formatter) => {
      const finalValue = formatter ? formatter(value) : value;
      setter(finalValue);
  };

  // Handler específico para mudança de UF, para resetar a cidade
  const handleUfChange = (e) => {
      const newUf = e.target.value;
      setUf(newUf);
      setCidade(""); // Reseta a cidade quando o estado muda
  };


  const inputClass =
    "w-full h-12 px-4 border border-[#E5E7EB] bg-[#F9FAFB] rounded-md placeholder-gray-400 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A4DD0]";
  
  const selectClass = `${inputClass} appearance-none`;

  return (
    <div className="flex flex-col gap-6">
      {/* Nome */}
      <div>
        <label className="block font-semibold text-gray-900 mb-1">
          Nome Completo*
        </label>
        <input
          className={inputClass}
          value={nome}
          onBlur={() => handleBlur("nome")}
          onChange={(e) => handleChange(setNome, e.target.value)}
          placeholder="Digite seu nome completo"
        />
        {touched.nome && errors.nome && (
          <p className="text-red-500 text-sm mt-1">{errors.nome}</p>
        )}
      </div>

      {/* E-mail */}
      <div>
        <label className="block font-semibold text-gray-900 mb-1">
          Email*
        </label>
        <input
          className={inputClass}
          type="email"
          value={email}
          onBlur={() => handleBlur("email")}
          onChange={(e) => handleChange(setEmail, e.target.value)}
          placeholder="seuemail@email.com"
        />
        {touched.email && errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email}</p>
        )}
      </div>

      {/* Nascimento & Telefone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className="block font-semibold text-gray-900 mb-1">
            Nascimento*
          </label>
          <input
            className={inputClass}
            type="date"
            max={maxBirthDate}
            value={nascimento}
            onBlur={() => handleBlur("nascimento")}
            onChange={(e) => handleChange(setNascimento, e.target.value)}
          />
          {touched.nascimento && errors.nascimento && (
            <p className="text-red-500 text-sm mt-1">
              {errors.nascimento}
            </p>
          )}
        </div>
        <div>
          <label className="block font-semibold text-gray-900 mb-1">
            Telefone*
          </label>
          <input
            className={inputClass}
            value={telefone}
            onBlur={() => handleBlur("telefone")}
            onChange={(e) =>
              handleChange(setTelefone, e.target.value, formatPhone)
            }
            placeholder="(00) 00000-0000"
          />
          {touched.telefone && errors.telefone && (
            <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>
          )}
        </div>
      </div>

      {/* CPF */}
      <div>
        <label className="block font-semibold text-gray-900 mb-1">
          CPF*
        </label>
        <input
          className={inputClass}
          value={cpf}
          onBlur={() => handleBlur("cpf")}
          onChange={(e) => handleChange(setCpf, e.target.value, formatCpf)}
          placeholder="000.000.000-00"
        />
        {touched.cpf && errors.cpf && (
          <p className="text-red-500 text-sm mt-1">{errors.cpf}</p>
        )}
      </div>

      {/* --- CAMPOS DE UF E CIDADE ATUALIZADOS --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Campo UF (Estado) */}
        <div>
          <label className="block font-semibold text-gray-900 mb-1">
            UF*
          </label>
          <select
            className={selectClass}
            value={uf}
            onBlur={() => handleBlur("uf")}
            onChange={handleUfChange}
          >
            <option value="">Selecione o estado</option>
            {estados.map((estado) => (
              // --- ALTERAÇÃO FINAL AQUI ---
              // O texto exibido na opção agora é a sigla do estado.
              <option key={estado.id} value={estado.sigla}>
                {estado.sigla}
              </option>
            ))}
          </select>
          {touched.uf && errors.uf && (
            <p className="text-red-500 text-sm mt-1">{errors.uf}</p>
          )}
        </div>
        {/* Campo Cidade */}
        <div>
          <label className="block font-semibold text-gray-900 mb-1">
            Cidade*
          </label>
          <select
            className={selectClass}
            value={cidade}
            onBlur={() => handleBlur("cidade")}
            onChange={(e) => handleChange(setCidade, e.target.value)}
            disabled={!uf}
          >
            <option value="">Selecione a cidade</option>
            {cidades.map((cidadeItem) => (
              <option key={cidadeItem.id} value={cidadeItem.nome}>
                {cidadeItem.nome}
              </option>
            ))}
          </select>
          {touched.cidade && errors.cidade && (
            <p className="text-red-500 text-sm mt-1">{errors.cidade}</p>
          )}
        </div>
      </div>
    </div>
  );
});